import { Grid, Input, NumberInput, SegmentedControl, Select, Text, TextInput, Textarea } from '@mantine/core';
import type { IOpcionSelect } from '../../../../modelos/CotizadorNBS';
import { campoAplica, type ICampoEspecificacion, type ISeccionEspecificaciones, type ListaEspecificacion, type ValorEspecificacion } from '../../utils/camposEspecificacion';

interface CamposEspecificacionesVistaProps<T> {
    /** Pestaña que se muestra (sus campos, en orden). */
    seccion: ISeccionEspecificaciones<T>;
    valores: T;
    alCambiar: (campo: Extract<keyof T, string>, valor: ValorEspecificacion) => void;
    errores?: Partial<Record<string, string>>;
    /** Marca los campos con asterisco. */
    obligatorios: boolean;
    largoTexto: number;
    largoAcabado?: number;
    deshabilitado: boolean;
    /** Opciones de los campos tipo 'lista', por lista del catálogo. */
    opciones?: Partial<Record<ListaEspecificacion, IOpcionSelect[]>>;
}

const unidadMm = <Text size="xs" c="dimmed" mr="xs" style={{ pointerEvents: 'none' }}>mm</Text>;

/** Una pestaña de especificaciones de un equipo (lo que sale en la tabla de la Oferta Comercial). */
export const CamposEspecificacionesVista = <T,>({
    seccion,
    valores,
    alCambiar,
    errores,
    obligatorios,
    largoTexto,
    largoAcabado = largoTexto,
    deshabilitado,
    opciones
}: CamposEspecificacionesVistaProps<T>) => {
    // Al apagar un switch se borran los campos que dependen de él (p. ej. entradas traseras sin doble acceso).
    const cambiarSwitch = (clave: Extract<keyof T, string>, activo: boolean) => {
        alCambiar(clave, activo);
        if (!activo) {
            seccion.campos.filter(c => c.dependeDe === clave).forEach(c => alCambiar(c.campo, null));
        }
    };

    const campo = (definicion: ICampoEspecificacion<T>) => {
        const { campo: clave, etiqueta, tipo, lista } = definicion;
        const valor = (valores as Record<string, ValorEspecificacion>)[clave];
        const aplica = campoAplica(definicion, valores);
        const comunes = {
            size: 'xs' as const,
            label: etiqueta,
            withAsterisk: obligatorios && aplica,
            error: errores?.[clave],
            disabled: deshabilitado || !aplica
        };

        if (tipo === 'switch') {
            // Sí/No con la etiqueta arriba, como los demás campos, para que quede alineado en la cuadrícula.
            return (
                <Input.Wrapper size="xs" label={etiqueta} withAsterisk={obligatorios}>
                    <SegmentedControl size="xs" fullWidth color="primary" disabled={deshabilitado}
                        data={[{ value: 'si', label: 'Sí' }, { value: 'no', label: 'No' }]}
                        value={valor === true ? 'si' : 'no'}
                        onChange={(v) => cambiarSwitch(clave, v === 'si')} />
                </Input.Wrapper>
            );
        }

        if (tipo === 'lista') {
            return (
                <Select {...comunes} searchable data={lista ? opciones?.[lista] ?? [] : []}
                    value={typeof valor === 'string' && valor !== '' ? valor : null}
                    onChange={(v) => alCambiar(clave, v ?? '')} />
            );
        }

        if (tipo === 'mm' || tipo === 'entero') {
            return (
                <NumberInput {...comunes} hideControls allowNegative={false} allowDecimal={false} min={0}
                    thousandSeparator={tipo === 'mm' ? '.' : undefined} decimalSeparator=","
                    value={typeof valor === 'number' ? valor : ''}
                    onChange={(v) => alCambiar(clave, v === '' ? null : Number(v))}
                    rightSection={tipo === 'mm' ? unidadMm : undefined} rightSectionWidth={35} />
            );
        }

        if (tipo === 'acabado') {
            return (
                <Textarea {...comunes} autosize minRows={1} maxRows={4} maxLength={largoAcabado}
                    value={String(valor ?? '')} onChange={(e) => alCambiar(clave, e.currentTarget.value)} />
            );
        }

        return (
            <TextInput {...comunes} maxLength={largoTexto}
                value={String(valor ?? '')} onChange={(e) => alCambiar(clave, e.currentTarget.value)} />
        );
    };

    return (
        <Grid>
            {seccion.campos.map(definicion => (
                <Grid.Col key={definicion.campo} span={{ base: 12, xs: 6, md: 4 }}>
                    {campo(definicion)}
                </Grid.Col>
            ))}
        </Grid>
    );
};
